import { useTranslation } from 'react-i18next';
import { services } from '@/mocks/services';
import { useInView } from '@/hooks/useInView';

export default function Services() {
  const { t } = useTranslation();
  const { ref, inView } = useInView<HTMLDivElement>(0.15);

  return (
    <section id="services" className="w-full bg-background-100 py-24 md:py-32">
      <div ref={ref} className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div className="max-w-3xl">
            <div className={`text-xs font-mono-tech tracking-widest uppercase text-foreground-600 mb-6 ${inView ? 'animate-fade-up' : 'opacity-0'}`}>
              {t('services.tag')}
            </div>
            <h2
              className={`font-heading text-foreground-950 text-4xl md:text-6xl lg:text-7xl leading-[1.02] font-semibold tracking-tight ${inView ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: '0.1s' }}
            >
              {t('services.title.line1')}
              <br />
              <span className="italic text-foreground-700">{t('services.title.line2')}</span>
            </h2>
          </div>
          <p
            className={`text-sm md:text-base text-foreground-600 max-w-xs md:text-right ${inView ? 'animate-fade-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.2s' }}
          >
            {t('services.desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((s, i) => (
            <div
              key={s.id}
              className={`group relative flex flex-col p-6 md:p-8 rounded-2xl border transition-all duration-500 cursor-default ${
                s.featured
                  ? 'bg-foreground-950 border-foreground-950 text-background-50 hover:bg-primary-500 hover:text-foreground-950 hover:border-primary-500'
                  : 'bg-background-50 border-background-200/70 hover:border-foreground-950'
              } ${inView ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: `${0.3 + i * 0.1}s` }}
            >
              <div className="flex items-center justify-between mb-14">
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center border transition-all ${
                    s.featured
                      ? 'border-background-50/20 bg-background-50/10 group-hover:bg-foreground-950 group-hover:border-foreground-950 group-hover:text-primary-500'
                      : 'border-foreground-200 bg-background-100 group-hover:bg-foreground-950 group-hover:text-primary-500 group-hover:border-foreground-950'
                  }`}
                >
                  <i className={`${s.icon} text-xl w-5 h-5 flex items-center justify-center`} />
                </div>
                <span
                  className={`text-[10px] font-mono-tech tracking-widest uppercase px-2 py-1 rounded-md ${
                    s.featured
                      ? 'bg-primary-500 text-foreground-950 group-hover:bg-foreground-950 group-hover:text-primary-500'
                      : 'bg-background-100 text-foreground-600'
                  }`}
                >
                  {t(s.tagKey)}
                </span>
              </div>

              <h3 className={`font-heading text-2xl md:text-3xl font-semibold tracking-tight mb-3 ${s.featured ? '' : 'text-foreground-950'}`}>
                {t(s.titleKey)}
              </h3>
              <p className={`text-sm leading-relaxed ${s.featured ? 'text-background-200/80 group-hover:text-foreground-800' : 'text-foreground-600'}`}>
                {t(s.descKey)}
              </p>

              <div className="mt-8 pt-6 border-t border-current/10 flex items-center justify-between">
                <span className={`text-xs font-mono-tech tracking-wider uppercase ${s.featured ? 'text-primary-500 group-hover:text-foreground-950' : 'text-foreground-500'}`}>
                  0{i + 1} / 04
                </span>
                <i
                  className={`ri-arrow-right-up-line text-lg w-5 h-5 flex items-center justify-center transition-transform group-hover:rotate-45 ${
                    s.featured ? 'text-primary-500 group-hover:text-foreground-950' : 'text-foreground-950'
                  }`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}